import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
// Import AuthProvider and useAuth from our new context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import TenantLookup from './components/auth/TenantLookup';
import QuartermasterLogin from './components/auth/QuartermasterLogin';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Purchases from './pages/Purchases';
import Assignments from './pages/Assignments';
import Onboarding from './pages/Onboarding';
import Reports from './pages/Reports';
import Users from './pages/Users';
import Admin from './pages/Admin';
import AuditLog from './pages/AuditLog';
import SystemAccess from './pages/SystemAccess';
import QuickAssign from './pages/QuickAssign';
import ProductCatalogPage from './pages/ProductCatalogPage';
import InitialInventoryPage from './pages/InitialInventoryPage';
import AssetsLandingPage from './pages/AssetsLandingPage';
import TrackedAssets from './pages/TrackedAssets';
import AssetCategories from './pages/AssetCategories';
import Verifications from './pages/Verifications';
import AssetReports from './pages/AssetReports';
import './App.css';
// 🔐 Import services
import { authService } from './services/authService';
import { configService, AppConfig } from './services/configService';
// 📊 Centralized logging
import { logger } from './utils/logger';
// Import simple debug utilities for development
import './utils/categoryDebug';
import { useMobileDetect } from './hooks/useMobileDetect';
// Create service-specific loggers
const appLogger = logger.createServiceLogger('App');
const configLogger = logger.createServiceLogger('Config');

interface TenantInfo {
  tenantId: string;
  organizationName: string;
  tenantSlug: string;
}

// Password Change Component
interface PasswordChangeProps {
  onChangePassword: (newPassword: string) => Promise<boolean>;
  error: string | null;
}

const PasswordChange: React.FC<PasswordChangeProps> = ({ onChangePassword, error }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    const success = await onChangePassword(newPassword);
    setLoading(false);

    if (!success) {
      // Error is handled by parent component
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Set New Password
          </h1>
          <p className="text-gray-600">
            Please choose a new password to complete your account setup
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                minLength={8}
              />
              <p className="mt-1 text-xs text-gray-500">
                At least 8 characters with uppercase, lowercase, number, and special character
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {(error || localError) && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error || localError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating Password...' : 'Set New Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string[];
  userRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole, 
  userRole 
}) => {
  if (requiredRole && userRole && !requiredRole.includes(userRole)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// SuperAdmin Redirect Component
interface SuperAdminRedirectProps {
  userRole: string;
  tenantId: string;
  children: React.ReactNode;
}

const SuperAdminRedirect: React.FC<SuperAdminRedirectProps> = ({ 
  userRole, 
  tenantId, 
  children 
}) => {
  // If SuperAdmin from SYSTEM tenant, redirect to admin
  if (userRole === 'SuperAdmin' && tenantId === 'SYSTEM') {
    return <Navigate to="/admin" replace />;
  }
  
  // Otherwise, show the dashboard
  return <>{children}</>;
};

// Main App Component - Now uses AuthContext
function App() {
  const { 
    user, 
    attributes, 
    loading, 
    error, 
    login, 
    logout, 
    passwordChangeRequired, 
    changePassword,
    tenantId,
    userRole,
    userName,
    organizationName,
    setOrganizationName
  } = useAuth();

  // Detect if user is on a phone (not tablet/desktop) - must be called before any early returns
  const isPhone = useMobileDetect();

  // Two-screen login flow state
  const [selectedTenant, setSelectedTenant] = useState<TenantInfo | null>(null);

  // Check for stored tenant on mount and refresh features
  useEffect(() => {
    const storedTenant = localStorage.getItem('qm_selectedTenant');
    if (storedTenant) {
      try {
        const tenant = JSON.parse(storedTenant);
        setSelectedTenant(tenant);
        // Store organization name when tenant is restored
        setOrganizationName(tenant.organizationName);
        appLogger.info('Restored tenant from storage', { tenant: tenant.organizationName });
        
        // Refresh features from API to ensure they're up-to-date
        if (tenant.tenantSlug) {
          import('./services/tenantManagementAPI').then(({ tenantManagementAPI }) => {
            tenantManagementAPI.getTenantBySlug(tenant.tenantSlug)
              .then(response => {
                if (response.success && response.data?.tenant?.features) {
                  const features = response.data.tenant.features || [];
                  localStorage.setItem('qm_tenantFeatures', JSON.stringify(features));
                  appLogger.info('Refreshed tenant features', { features });
                }
              })
              .catch(err => {
                appLogger.error('Failed to refresh tenant features', undefined, err);
              });
          });
        }
      } catch (err) {
        appLogger.error('Failed to parse stored tenant', undefined, err as Error);
        localStorage.removeItem('qm_selectedTenant');
      }
    }
  }, [setOrganizationName]);

  // Development debug info
  useEffect(() => {
    if (user && process.env.NODE_ENV === 'development') {
      appLogger.info('Debug mode enabled for development', {
        availableFunctions: [
          'window.testAddItemModal()',
          'authService.getLastTokenInfo()',
          'window.qmConfig'
        ]
      });
      
      // Make auth service available for debugging
      (window as any).authService = authService;
    }
  }, [user]);

  const handleTenantSelected = (tenant: TenantInfo) => {
    setSelectedTenant(tenant);
    // Store organization name when tenant is selected
    setOrganizationName(tenant.organizationName);
  };

  const handleChangeSite = () => {
    // Clear selected tenant and go back to tenant lookup
    localStorage.removeItem('qm_selectedTenant');
    localStorage.removeItem('organizationName');
    setSelectedTenant(null);
    appLogger.info('User changed site, returning to tenant selection');
  };

  const handleLogout = async () => {
    await logout();
    // Don't clear selected tenant on logout - user can login again to same tenant
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show password change screen if required
  if (passwordChangeRequired) {
    return <PasswordChange onChangePassword={changePassword} error={error} />;
  }

  // Two-screen login flow
  if (!user) {
    // Screen 1: Tenant Selection (if no tenant selected)
    if (!selectedTenant) {
      return <TenantLookup onTenantSelected={handleTenantSelected} />;
    }

    // Screen 2: User Login (with tenant selected)
    return (
      <QuartermasterLogin 
        tenant={selectedTenant}
        onLogin={login} 
        onLogout={handleLogout}
        onChangeSite={handleChangeSite}
        error={error} 
      />
    );
  }

 // Get values from context
  const effectiveUserRole = userRole || '';
  const effectiveTenantId = tenantId || '';
  const effectiveUserName = userName || '';
  const effectiveOrganizationName = organizationName || effectiveTenantId;

  // Phone users go directly to Quick Assign
  if (isPhone) {
    return (
      <Router>
        <Routes>
          <Route 
            path="*" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin', 'InventoryManager', 'QuickAssign']} 
                userRole={effectiveUserRole}
              >
                <QuickAssign />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        <Route 
          path="/" 
          element={
            <Layout
              userName={effectiveUserName}
              userRole={effectiveUserRole}
              tenantId={effectiveTenantId}
              organizationName={effectiveOrganizationName}
              onSignOut={handleLogout}
            />
          }
        >
          {/* Dashboard - With SuperAdmin redirect */}
          <Route 
            index
            element={
              <SuperAdminRedirect userRole={effectiveUserRole} tenantId={effectiveTenantId}>
                <Dashboard 
                  userRole={effectiveUserRole} 
                  tenantId={effectiveTenantId}
                  organizationName={effectiveOrganizationName}
                />
              </SuperAdminRedirect>
            } 
          />
          
          {/* Inventory - Available to all authenticated users */}
          <Route 
            path="inventory" 
            element={<Inventory />} 
          />
          
          {/* Purchases - Available to OrgAdmin and InventoryManager */}
          <Route 
            path="purchases" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin', 'InventoryManager']} 
                userRole={effectiveUserRole}
              >
                <Purchases />
              </ProtectedRoute>
            } 
          />
          
          {/* Product Catalog - Available to OrgAdmin and SuperAdmin */}
          <Route 
            path="products" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin']} 
                userRole={effectiveUserRole}
              >
                <ProductCatalogPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Initial Inventory - Available to OrgAdmin and SuperAdmin */}
          <Route 
            path="initial-inventory" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin']} 
                userRole={effectiveUserRole}
              >
                <InitialInventoryPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Assignments - Available to OrgAdmin and InventoryManager roles */}
          <Route 
            path="assignments" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin', 'InventoryManager']} 
                userRole={effectiveUserRole}
              >
                <Assignments />
              </ProtectedRoute>
            } 
          />
          
          {/* Onboarding Management - Available to OrgAdmin and InventoryManager roles */}
          <Route 
            path="onboarding" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin', 'InventoryManager']} 
                userRole={effectiveUserRole}
              >
                <Onboarding />
              </ProtectedRoute>
            } 
          />
          
          {/* Onboarding Session View - Available to OrgAdmin and InventoryManager roles */}
          <Route 
            path="onboarding/:sessionId" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin', 'InventoryManager']} 
                userRole={effectiveUserRole}
              >
                <Onboarding />
              </ProtectedRoute>
            } 
          />
          
        {/* Reports - Available to all authenticated users */}
          <Route path="reports" element={<Reports />} />
          
          {/* Assets Module - Landing Page */}
          <Route 
            path="assets" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin', 'InventoryManager']} 
                userRole={effectiveUserRole}
              >
                <AssetsLandingPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Assets Module - Tracked Assets */}
          <Route 
            path="assets/tracked" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin', 'InventoryManager']} 
                userRole={effectiveUserRole}
              >
                <TrackedAssets />
              </ProtectedRoute>
            } 
          />
          
          {/* Assets Module - Categories */}
          <Route 
            path="assets/categories" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin']} 
                userRole={effectiveUserRole}
              >
                <AssetCategories />
              </ProtectedRoute>
            } 
          />
          
          {/* Assets Module - Verifications */}
          <Route 
            path="assets/verifications" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin', 'InventoryManager']} 
                userRole={effectiveUserRole}
              >
                <Verifications />
              </ProtectedRoute>
            } 
          />
          
          {/* Assets Module - Reports */}
          <Route 
            path="assets/reports" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin', 'InventoryManager']} 
                userRole={effectiveUserRole}
              >
                <AssetReports />
              </ProtectedRoute>
            } 
          />
          
          {/* Users - Available to OrgAdmin and InventoryManager roles */}
          <Route 
            path="users" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin', 'InventoryManager']} 
                userRole={effectiveUserRole}
              >
                <Users />
              </ProtectedRoute>
            } 
          />
          
          {/* System Access - Available to SuperAdmin and OrgAdmin */}
          <Route 
            path="system-access" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin']} 
                userRole={effectiveUserRole}
              >
                <SystemAccess />
              </ProtectedRoute>
            } 
          />
          
          {/* Audit Log - Available to SuperAdmin and OrgAdmin */}
          <Route 
            path="audit" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin']} 
                userRole={effectiveUserRole}
              >
                <AuditLog />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin - Available to SuperAdmin and OrgAdmin */}
          <Route 
            path="admin" 
            element={
              <ProtectedRoute 
                requiredRole={['SuperAdmin', 'OrgAdmin']} 
                userRole={effectiveUserRole}
              >
                <Admin 
                  userRole={effectiveUserRole}
                  tenantId={effectiveTenantId}
                  userName={effectiveUserName}
                />
              </ProtectedRoute>
            } 
          />
      </Route>
        
        {/* Quick Assign - Mobile optimized assignment page (standalone, no sidebar) */}
        <Route 
          path="/quick-assign" 
          element={
            <ProtectedRoute 
              requiredRole={['SuperAdmin', 'OrgAdmin', 'InventoryManager', 'QuickAssign']} 
              userRole={effectiveUserRole}
            >
              <QuickAssign />
            </ProtectedRoute>
          } 
        />
        
        {/* Fallback - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

// Configuration initialization and App wrapper
const AppWithConfig: React.FC = () => {
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    const initializeConfig = async () => {
      try {
        configLogger.info('Initializing configuration service');
        const config = await configService.initialize();
        
        // Validate configuration
        if (!configService.validateConfig(config)) {
          throw new Error('Configuration validation failed');
        }

        // Configure Amplify with loaded configuration
        configLogger.info('Configuring Amplify with environment config', { 
          environment: config.environment 
        });
        (Amplify as any).configure({
          Auth: {
            Cognito: {
              region: config.cognito.region,
              userPoolId: config.cognito.userPoolId,
              userPoolClientId: config.cognito.userPoolClientId,
            }
          }
        });

        configLogger.info('Configuration and Amplify initialization complete', { 
          environment: config.environment 
        });
        
        // Make config available globally for debugging in development
        if (config.environment === 'development') {
          (window as any).qmConfig = config;
          configLogger.debug('Config available as window.qmConfig for debugging');
        }

      } catch (error) {
        configLogger.error('Configuration initialization failed', undefined, error as Error);
        setConfigError(error instanceof Error ? error.message : 'Configuration failed');
      } finally {
        setConfigLoading(false);
      }
    };

    initializeConfig();
  }, []);

  // Show loading while initializing configuration
  if (configLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing application...</p>
        </div>
      </div>
    );
  }

  // Show error if configuration failed
  if (configError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Configuration Error</h1>
          <p className="text-gray-600 mb-4">{configError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Wrap App with AuthProvider
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
};

// Export the wrapped component as default
export default AppWithConfig;
